import { useQuery } from '@tanstack/react-query';
import { StatusCard } from '../../components/admin/cards';
import { DataTable } from '../../components/admin/tables';
import { api } from '../../services/api';

export default function AdminSystem() {
  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['admin-system-health'],
    queryFn: () => api.getAdminSystemHealth(),
    refetchInterval: 30000,
  });

  const { data: seedRuns, isLoading: seedRunsLoading } = useQuery({
    queryKey: ['admin-seed-runs'],
    queryFn: () => api.getAdminSeedRuns(20),
  });

  const { data: errorLogs } = useQuery({
    queryKey: ['admin-error-logs'],
    queryFn: () => api.getAdminErrorLogs('24h', 10),
  });

  const seedRunColumns = [
    {
      key: 'status',
      header: 'Status',
      render: (run: { status: string }) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          run.status === 'completed' ? 'bg-green-100 text-green-700' :
          run.status === 'running' ? 'bg-blue-100 text-blue-700' :
          run.status === 'failed' ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {run.status}
        </span>
      ),
    },
    {
      key: 'run_type',
      header: 'Type',
    },
    {
      key: 'seed_id',
      header: 'Seed ID',
      render: (run: { seed_id: number | null }) => run.seed_id || '-',
    },
    {
      key: 'store_id',
      header: 'Store',
      render: (run: { store_id: string | null }) => run.store_id || '-',
    },
    {
      key: 'products_found',
      header: 'Found',
    },
    {
      key: 'started_at',
      header: 'Started',
      render: (run: { started_at: string }) => (
        <span className="text-sm text-gray-500">
          {new Date(run.started_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (run: { started_at: string; completed_at: string | null }) => {
        if (!run.completed_at) return '-';
        const duration = new Date(run.completed_at).getTime() - new Date(run.started_at).getTime();
        const minutes = Math.floor(duration / 60000);
        const seconds = Math.floor((duration % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatusCard
          title="API"
          status={healthLoading ? 'unknown' : health?.api.status === 'healthy' ? 'healthy' : 'error'}
          details={health ? [
            { label: 'Uptime', value: health.api.uptime },
            { label: 'Response Time', value: `${health.api.responseTime}ms` },
          ] : []}
        />
        <StatusCard
          title="Database"
          status={healthLoading ? 'unknown' : health?.database.status === 'healthy' ? 'healthy' : 'error'}
          details={health ? [
            { label: 'Latency', value: `${health.database.latency}ms` },
          ] : []}
        />
        <StatusCard
          title="Seed Browsers"
          status={
            healthLoading ? 'unknown' :
            (health?.seeds.successRate || 0) >= 90 ? 'healthy' :
            (health?.seeds.successRate || 0) >= 70 ? 'warning' : 'error'
          }
          details={health ? [
            { label: 'Success Rate', value: `${health.seeds.successRate}%` },
            { label: 'Running', value: health.seeds.running },
            { label: 'Stores Covered', value: health.seeds.storesCovered },
          ] : []}
        />
      </div>

      {/* Seed Browser Summary */}
      {health?.seeds && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Seed Browser Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{health.seeds.runsThisWeek}</p>
              <p className="text-sm text-gray-500">Runs This Week</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{health.seeds.successful}</p>
              <p className="text-sm text-gray-500">Successful</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{health.seeds.failed}</p>
              <p className="text-sm text-gray-500">Failed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{health.seeds.running}</p>
              <p className="text-sm text-gray-500">Running</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{health.seeds.storesCovered}</p>
              <p className="text-sm text-gray-500">Stores Covered</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Seed Runs */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Seed Runs</h3>
        <DataTable
          columns={seedRunColumns}
          data={seedRuns || []}
          loading={seedRunsLoading}
        />
      </div>

      {/* Error Logs */}
      {errorLogs && errorLogs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Errors (24h)</h3>
          <div className="space-y-3">
            {errorLogs.map((error) => (
              <div key={error.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <span className="font-medium text-red-800">{error.error_type}</span>
                  <span className="text-xs text-red-600">
                    {new Date(error.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-red-700 mt-1">{error.message}</p>
                {error.request_path && (
                  <p className="text-xs text-red-500 mt-1">
                    {error.request_method} {error.request_path}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
